import * as ko from 'knockout';

import { app } from '../app';
import i18n from '../i18n';
import { Deferred } from '../utils/deferred';
import * as measurementMetaApi from '../api/measurement_metas';
import { ListRequestParams, getContentRange } from '../api/request';
import * as trialsApi from '../api/trials';
import {
  PollTaskCancelled,
  PollTaskConnectionLost,
  PollTaskHandle,
  pollTaskUntilDone,
  TaskCopyDetails,
} from '../api/tasks';
import { ListLoaderDelegate, ListLoader, ItemSelectionTracker } from './list_loader';
import { FilterDelegate } from './list_filters';
import { deflateList } from '../api/serialization';
import { CountryData, listForTrials } from '../api/countries';
import * as regionsApi from '../api/regions';
import * as seasonsApi from '../api/seasons';
import * as usersApi from '../api/users';
import * as cropsApi from '../api/crops';
import * as dimensionsApi from '../api/dimensions';
import { customersApi, partnersApi, CustomerData, PartnerData } from '../api/organizations';
import { DimensionData } from '../api/dimensions';
import { TRIAL_TYPE_SLUG } from '../api/dimension_metas';
import { I18nText, translate } from '../i18n_text';

const template = require('raw-loader!../../templates/components/copy_trait_to_trials.html').default;

const MAX_SELECTION = 500;

class CopyTraitToTrials {
  private result: Deferred<unknown>;
  private measurementMetaId: string;
  private measurementMetaNameSlug: string;
  private pollHandle: PollTaskHandle | null = null;

  title: string;
  phase = ko.observable<'picker' | 'running' | 'done'>('picker');

  nameSearch = ko.observable('').extend({ throttle: 300 });

  cropFilter = ko.observableArray<DimensionData>(null);
  countryFilter = ko.observableArray<CountryData>(null);
  regionFilter = ko.observableArray<regionsApi.RegionData>(null);
  ownerFilter = ko.observableArray<usersApi.UserData>(null);
  customerFilter = ko.observableArray<CustomerData>(null);
  partnerFilter = ko.observableArray<PartnerData>(null);
  trialTypeFilter = ko.observableArray<DimensionData>(null);
  seasonFilter = ko.observableArray<seasonsApi.SeasonData>(null);

  private trialsListLoader: ListLoader<trialsApi.TrialData> | null = null;

  private pageItems = ko.observableArray<trialsApi.TrialData>([]);
  selectionTracker = new ItemSelectionTracker(this.pageItems, { maxSelection: MAX_SELECTION });

  selectAllInFlight = ko.observable(false);

  private progressPercent = ko.observable(0);
  progressText = ko.pureComputed(() => {
    const perc = this.progressPercent();
    return i18n.t('Copying trait ({{perc}}%)...', { perc })();
  });
  resultDetails = ko.observable<TaskCopyDetails | null>(null);
  resultError = ko.observable('');
  succeededText = ko.pureComputed(() => {
    const count = this.resultDetails()?.succeeded.length ?? 0;
    return i18n.t('Copied to {{count}} trial(s):', { count })();
  });

  submitDisabled = ko.pureComputed(
    () =>
      this.selectionTracker.selectedCount() === 0 ||
      this.selectionTracker.selectionExceedsMax() ||
      this.selectAllInFlight()
  );
  submitButtonLabel = ko.pureComputed(() => {
    const selectedCount = this.selectionTracker.selectedCount();
    if (selectedCount === 0) return i18n.t('Copy to selected trials')();
    return i18n.t('Copy to {{count}} trial(s)', { count: selectedCount })();
  });

  private listFilterParams = (): trialsApi.TrialListFilters => ({
    search: this.nameSearch() || undefined,
    crop_ids: deflateList(this.cropFilter),
    country_ids: deflateList(this.countryFilter),
    region_ids: deflateList(this.regionFilter),
    user_ids: deflateList(this.ownerFilter),
    customer_ids: deflateList(this.customerFilter),
    partner_ids: deflateList(this.partnerFilter),
    trial_type_ids: deflateList(this.trialTypeFilter),
    season_ids: deflateList(this.seasonFilter),
    states: ['draft', 'preview', 'active'],
    template: false,
    sort_by: 'name',
    sort_direction: 'asc',
    missing_trait_slugs: [this.measurementMetaNameSlug],
    // The copy runs through the wizard's library derive, so a manual-mode
    // trial cannot take a library trait.
    edit_modes: ['library'],
  });

  // Knockout's checkbox binding compares by reference identity, so stale row
  // refs must be replaced with the freshly-loaded ones for their tick to render.
  private reconcileSelectedRefs = (pageRows: trialsApi.TrialData[]) => {
    const tracker = this.selectionTracker;
    const previousSelection = tracker.selectedItems();
    if (previousSelection.length === 0) return previousSelection;
    const byId = new Map(pageRows.map((row) => [row.id, row]));
    const reconciled = previousSelection.map((row) => byId.get(row.id) ?? row);
    const changed = reconciled.some((row, index) => row !== previousSelection[index]);
    if (changed) tracker.selectedItems(reconciled);
    return reconciled;
  };

  trialsListDelegate: ListLoaderDelegate<trialsApi.TrialData> = {
    onReady: (loader) => {
      this.trialsListLoader = loader;
      loader.items.subscribe((newItems) => {
        this.pageItems(newItems);
        this.reconcileSelectedRefs(newItems);
      });
      this.pageItems(loader.items());
    },
    fetch: (params: ListRequestParams) =>
      trialsApi.list(
        {
          ...this.listFilterParams(),
          ...params,
        },
        {
          injectTenant: true,
          onHeadersReceived: (headers) => {
            try {
              this.selectionTracker.total(getContentRange(headers).total);
            } catch {
              this.selectionTracker.total(null);
            }
          },
        }
      ),
    instantiate: (data) => data,
    canRemove: () => false,
    newFilters: [
      { title: i18n.t('Crop')(), entities: this.cropFilter, list: cropsApi.list },
      { title: i18n.t('Country')(), entities: this.countryFilter, list: listForTrials },
      { title: i18n.t('Region')(), entities: this.regionFilter, list: regionsApi.list },
      { title: i18n.t('Owner')(), entities: this.ownerFilter, list: usersApi.list },
      { title: i18n.t('Customer')(), entities: this.customerFilter, list: customersApi.list },
      { title: i18n.t('Partner')(), entities: this.partnerFilter, list: partnersApi.list },
      {
        title: i18n.t('Type')(),
        entities: this.trialTypeFilter,
        list: (params) => dimensionsApi.list(TRIAL_TYPE_SLUG, {}, params),
      },
      { title: i18n.t('Season')(), entities: this.seasonFilter, list: seasonsApi.list },
    ] as FilterDelegate[],
  };

  private subscriptions: ko.Subscription[] = [];

  constructor(params: {
    measurementMetaId: string;
    measurementMetaNameSlug: string;
    measurementMetaName: string;
    result: Deferred<unknown>;
  }) {
    this.result = params.result;
    this.measurementMetaId = params.measurementMetaId;
    this.measurementMetaNameSlug = params.measurementMetaNameSlug;
    this.title = i18n.t('Copy "{{name}}" to trials', { name: params.measurementMetaName })();

    this.subscriptions.push(
      this.nameSearch.subscribe(() => {
        this.selectionTracker.clear();
        this.trialsListLoader?.refresh();
      })
    );
    const filterObservables: ko.Subscribable<unknown>[] = [
      this.cropFilter,
      this.countryFilter,
      this.regionFilter,
      this.ownerFilter,
      this.customerFilter,
      this.partnerFilter,
      this.trialTypeFilter,
    ];
    for (const obs of filterObservables) {
      this.subscriptions.push(obs.subscribe(() => this.selectionTracker.clear()));
    }
  }

  dispose() {
    for (const s of this.subscriptions) s.dispose();
    this.pollHandle?.cancel();
    this.pollHandle = null;
  }

  onClose = () => {
    this.result.resolve(null);
  };

  toggleRow = (item: trialsApi.TrialData, event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('input, a')) return true;
    this.selectionTracker.toggle(item);
    return true;
  };

  selectAllMatchingFilters = async () => {
    const tracker = this.selectionTracker;
    if (this.selectAllInFlight()) return;

    this.selectAllInFlight(true);
    try {
      const trials = await trialsApi.list({
        ...this.listFilterParams(),
        limit: MAX_SELECTION,
        offset: 0,
      });
      tracker.selectedItems(trials);
      this.reconcileSelectedRefs(this.pageItems());
      tracker.selectAll();
    } finally {
      this.selectAllInFlight(false);
    }
  };

  submit = async () => {
    if (this.submitDisabled()) return;
    const selected = this.selectionTracker.selectedItems();
    const ids = selected.map((trial) => String(trial.id ?? '')).filter((id) => id !== '');

    this.phase('running');
    this.progressPercent(0);
    this.resultError('');

    try {
      const { id } = await measurementMetaApi.createCopies(this.measurementMetaId, ids);
      this.pollHandle = pollTaskUntilDone(id, {
        onProgress: (percent) => this.progressPercent(percent),
      });
    } catch (error) {
      this.resultError(i18n.t('Failed to start copy. Please try again.')());
      this.phase('done');
      return;
    }

    try {
      const res = await this.pollHandle.promise;
      if (res.error_msg) {
        this.resultError(res.error_msg);
      }
      this.resultDetails(res.details);
      this.phase('done');
    } catch (error) {
      if (error instanceof PollTaskConnectionLost) {
        this.resultError(
          i18n.t(
            'Lost connection while waiting for the copy to finish. Please reload to check the result.'
          )()
        );
        this.phase('done');
      }
      // PollTaskCancelled: dispose() ran, do nothing.
      if (!(error instanceof PollTaskCancelled)) throw error;
    } finally {
      this.pollHandle = null;
    }
  };
}

export function openCopyTraitToTrials(
  measurementMetaId: string,
  measurementMetaNameSlug: string,
  measurementMetaName: string | I18nText,
) {
  const displayName =
    typeof measurementMetaName === 'string'
      ? measurementMetaName
      : translate(measurementMetaName) || '';
  app.formsStackController.push({
    title: i18n.t('Copy to trials')(),
    name: 'copy-trait-to-trials',
    isBig: true,
    className: 'copy-trait-to-trials-popup',
    params: {
      measurementMetaId,
      measurementMetaNameSlug,
      measurementMetaName: displayName,
      result: new Deferred<unknown>(),
    },
  });
}

ko.components.register('copy-trait-to-trials', {
  viewModel: CopyTraitToTrials,
  template,
});
